<script setup>
import AppLayout from "@/layouts/AppLayout.vue";
import {computed, onMounted, ref} from "vue";
import {GetOrganisation} from "@/services/organisationService.js";
import {useRoute} from "vue-router";
import {GetApp, GetAppAnalytics} from "@/services/appService.js";
import {getContext} from "@/services/context.js";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card/index.js";
import { Bar } from 'vue-chartjs'
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select/index.js";
import {Input} from "@/components/ui/input/index.js";
import {Label} from "@/components/ui/label/index.js";
import {Skeleton} from "@/components/ui/skeleton/index.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const route = useRoute();

const organisation = ref(null);
const app = ref(null);
const analytics = ref(null);
const isLoading = ref(true);

const dateRange = ref('7d');
const startDate = ref(new Date());
const endDate = ref(new Date());

// Set initial dates for 7 days
startDate.value.setDate(startDate.value.getDate() - 7);

const dateRanges = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range' }
];

async function fetchAnalytics() {
  isLoading.value = true;
  try {
    analytics.value = await GetAppAnalytics(getContext(route), startDate.value, endDate.value);
  } finally {
    isLoading.value = false;
  }
}

function updateDateRange(value) {
  dateRange.value = value;
  const end = new Date();
  const start = new Date();
  
  switch (value) {
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '30d':
      start.setDate(start.getDate() - 30);
      break;
    case '90d':
      start.setDate(start.getDate() - 90);
      break;
    case 'custom':
      // Keep current custom dates
      return;
  }
  
  startDate.value = start;
  endDate.value = end;
  fetchAnalytics();
}

onMounted(async () => {
  organisation.value = await GetOrganisation(getContext(route));
  app.value = await GetApp(getContext(route), route.params.appId);
  await fetchAnalytics();
});

const chartData = computed(() => {
  if (!analytics.value) return null;
  
  return {
    labels: analytics.value.map(item => new Date(item.date).toLocaleDateString()),
    datasets: [
      {
        label: 'Successful Hits',
        data: analytics.value.map(item => item.hitCount),
        backgroundColor: '#22c55e',
        borderRadius: 4,
      },
      {
        label: 'Misses',
        data: analytics.value.map(item => item.missCount),
        backgroundColor: '#ef4444',
        borderRadius: 4,
      },
      {
        label: 'Errors',
        data: analytics.value.map(item => item.errorCount),
        backgroundColor: '#f59e0b',
        borderRadius: 4,
      }
    ]
  }
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
    }
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0
      }
    }
  }
};
</script>

<template>
  <AppLayout :organisation="organisation" :app="app">
    <div class="flex-1 space-y-4">
      <Card v-if="!isLoading && analytics">
        <CardHeader>
          <div class="flex items-center justify-between">
            <div>
              <CardTitle>Analytics</CardTitle>
              <CardDescription>Panel views over time</CardDescription>
            </div>
            <div class="flex items-center gap-4">
              <Select v-model="dateRange" @update:modelValue="updateDateRange">
                <SelectTrigger class="w-[180px]">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem v-for="range in dateRanges" :key="range.value" :value="range.value">
                    {{ range.label }}
                  </SelectItem>
                </SelectContent>
              </Select>
              
              <div v-if="dateRange === 'custom'" class="flex items-center gap-2">
                <div class="flex items-center gap-2">
                  <Label>From</Label>
                  <Input type="date" v-model="startDate" @change="fetchAnalytics" />
                </div>
                <div class="flex items-center gap-2">
                  <Label>To</Label>
                  <Input type="date" v-model="endDate" @change="fetchAnalytics" />
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div class="h-[500px]">
            <Bar v-if="chartData" :data="chartData" :options="chartOptions" />
          </div>
        </CardContent>
      </Card>

      <div v-if="isLoading" class="space-y-4">
        <Card>
          <CardHeader>
            <Skeleton class="h-8 w-[200px]" />
            <Skeleton class="h-4 w-[300px]" />
          </CardHeader>
          <CardContent>
            <Skeleton class="h-[500px]" />
          </CardContent>
        </Card>
      </div>
    </div>
  </AppLayout>
</template> 